import { createElement } from "react";
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import store from "../application/stores/store"

// Store root instances for proper unmounting
const renderedRoots = new WeakMap()

function getValidPaths(contexts) {
  return contexts.reduce(
    (res, context) => res.concat(context.keys()),
    [],
  )
}

function requireComponent(requirePath, contexts) {
  for (const context of contexts) {
    try {
      // Loading a component will raise an error if it fails
      // but we want to be able to try multiple contexts
      return context(requirePath)
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("Cannot find module")
      ) {
        continue
      }

      throw error
    }
  }
}

function getConstructor(className, contexts) {
  const parts = className.split(".")
  const filename = parts.shift()
  const keys = parts
  const requirePath = `./${filename}`

  // Load the module:
  let component = requireComponent(requirePath, contexts)

  if (!component) {
    console.error(`Cannot find module "${requirePath}"`) // eslint-disable-line
    console.log("Valid paths are:", getValidPaths(contexts)) // eslint-disable-line
    return
  }

  // Then access each key:
  keys.forEach(function (k) {
    component = component[k]
  })

  // support `export default`
  if (component.__esModule) {
    component = component["default"]
  }
  return component
}

export function mountComponents(...contexts) {
  const nodes = document.querySelectorAll("[data-react-component]")

  for (let i = 0; i < nodes.length; ++i) {
    const node = nodes[i]
    const className = node.getAttribute("data-react-component")

    // A re-scan can reach a node that is already mounted: a modal announces its content when it
    // loads and again when its form submits, and every scan walks the whole document. Tearing the
    // component down and building it again would throw away the state it holds - for Select, the
    // values the user just picked, moments before the form is read. These nodes all come from a
    // document query, so they are still on the page; taking them down is unmountComponents' job.
    if (renderedRoots.has(node)) continue

    const constructor = getConstructor(className, contexts)

    if (!constructor) {
      console.error("react-component not found", className)
      continue
    }

    const propsJson = node.getAttribute("data-react-props")
    const props = propsJson && JSON.parse(propsJson)

    // Wrap component with Redux Provider
    const component = createElement(
      Provider,
      { store },
      createElement(constructor, props)
    )
    const root = createRoot(node)
    renderedRoots.set(node, root)
    root.render(component)
  }
}

// `root` scopes the teardown. A modal unmounts what it is about to throw away; everything else on
// the page has to survive that, so it must not simply walk the document.
export function unmountComponents(root = document) {
  const nodes = root.querySelectorAll("[data-react-component]")
  for (let i = 0; i < nodes.length; ++i) {
    const node = nodes[i]
    const mounted = renderedRoots.get(node)
    if (mounted) {
      mounted.unmount()
      renderedRoots.delete(node)
    }
  }
}
